import {
  CloseInfo,
  errorsToObjectErrors,
  Flex,
  IconButton,
  InputGroup,
  InputGroupBreak,
  InputRow,
  ModelInput,
  ModelPhoneInput,
  ModelSelect,
  SecondaryButton,
  useDeleteAlert,
} from '@equifinance/shared';
import styled from 'styled-components';
import { Fragment } from 'react';
import { useAccountContext } from '../../../../contexts';
import {
  Applicant,
  ContactDetails,
  ContactDetailsType,
} from '../../../../contexts/account/Account';
import Section from '../../components/Section';
import { DeleteIcon, PlusIcon } from '../../components/Icons';
import { useProgressStatusByInitiator } from '../../../../contexts/progressStatus/ProgressStatusProvider';

const AddContactDetailsContainer = styled(Flex)`${({ theme }) => `
  padding: ${theme.padding[4]} 0;
  justify-content: end;
`}`;

const DeleteIconHideable = styled(IconButton) <{ show?: boolean }>`${({ show = false }) => `
  ${!show ? 'visibility: hidden;' : ''}
`}`;
export default function ApplicantContactDetails({
  applicant,
  applicantIndex,
}: {
  applicant: Applicant;
  applicantIndex: number;
}) {
  const { account, appendToAccountArray, deleteFromAccountArray } = useAccountContext();

  const { applicants, errors, warnings } = account;

  const nonPropertyErrors = errorsToObjectErrors(errors?.[applicant?.contacts?.id] ?? []);
  const addContactInProgress = useProgressStatusByInitiator('addContact-button');

  const addContact = () => {
    if (addContactInProgress) return;
    appendToAccountArray(`applicants[${applicantIndex}].contacts.contactDetails`, {}, 'addContact-button');
  };

  const deleteAlert = useDeleteAlert();

  const deletePosition = (contactDetail: ContactDetails) => {
    deleteAlert({
      onConfirm: () => deleteFromAccountArray(`applicants[${applicantIndex}].contacts.contactDetails`, contactDetail?.id),
      text: 'Are you sure you want to delete contact details?',
      content: 'This action cannot be undone.',
    });
  };

  const renderContactDetail = (contactDetail: ContactDetails, showDelete: boolean, index: number) => {
    const firstElement = index === 0;
    return (
      <Fragment key={contactDetail.id}>
        {!firstElement && (<InputGroupBreak />)}
        <InputGroup>
          <InputRow>
            <ModelSelect
              options={ContactDetailsType}
              field={`applicants[${applicantIndex}].contacts.contactDetails.[${index}].type`}
              required
              disabled={firstElement}
            />
            {([
              'HomeNumber',
              'PersonalMobileNumber',
              'WorkMobileNumber',
            ]).includes(contactDetail.type) ? (
              <>
                <ModelPhoneInput
                  field={`applicants[${applicantIndex}].contacts.contactDetails.[${index}].info`}
                  required
                />
              </>
              ) : (
                <ModelInput
                  field={`applicants[${applicantIndex}].contacts.contactDetails.[${index}].info`}
                  required
                />
              )}
            <DeleteIconHideable onClick={() => deletePosition(contactDetail)} show={showDelete}><DeleteIcon /></DeleteIconHideable>
          </InputRow>
        </InputGroup>
      </Fragment>
    );
  };

  return (
    <Section
      id={applicant?.contacts?.id}
      key={applicant?.contacts?.id}
      title="Contact details"
      level={1}
      titleIcon={<></>}
      errors={errors}
      warnings={warnings}
    >
      {nonPropertyErrors?.length === 0 && (
        <CloseInfo>
          Please provide an email address and
          one phone number – these must be unique per Applicant.
        </CloseInfo>
      )}
      <AddContactDetailsContainer>
        <SecondaryButton
          disabled={addContactInProgress}
          small
          onClick={addContact}
        >
          <PlusIcon />
          Add contact details
        </SecondaryButton>
      </AddContactDetailsContainer>

      {applicants[applicantIndex].contacts?.contactDetails?.map((contactDetail, index) => (
        <>
          {renderContactDetail(contactDetail, index > 1, index)}
        </>
      ))}
    </Section>
  );
}
